import { CredentialTileSlotLayoutV1 } from "../../../../types/credential";
import { GroupedAssignments } from "../types/common";
import { getUniqueLegacyGridTileIds, normalizeLegacyGridSelection } from "./legacyGridAdapter";
import { isSupportedCredGridPair } from "./gridFormats";

type CreateTileSlotLayoutParams = {
  tileIds: string[];
  rowsPerSlide: number | undefined;
  colsPerRow: number | undefined;
  selectionTileCount?: number;
};

type ReadTileSlotLayoutParams = {
  tileSlotLayout: unknown;
  availableTileIds?: string[];
};

type MoveTileInSlotLayoutParams = {
  activeId: string;
  overId: string;
  tileSlotLayout: CredentialTileSlotLayoutV1;
};

const DEFAULT_MAX_PERSISTED_SLOT_COUNT = 500;
const DEFAULT_MAX_PERSISTED_TILE_ID_LENGTH = 10_000;
const GROUPED_ASSIGNMENT_BASE_KEYS = new Set(["clientCompanyId", "id"]);

const isValidTileSlotLayoutHeader = (tileSlotLayout: unknown): tileSlotLayout is CredentialTileSlotLayoutV1 => (
  !!tileSlotLayout
  && typeof tileSlotLayout === "object"
  && (tileSlotLayout as CredentialTileSlotLayoutV1).version === 1
  && isSupportedCredGridPair({
    columns: (tileSlotLayout as CredentialTileSlotLayoutV1).cols_per_row,
    rows: (tileSlotLayout as CredentialTileSlotLayoutV1).rows_per_slide,
  })
  && Array.isArray((tileSlotLayout as CredentialTileSlotLayoutV1).slots)
);

const getUniqueTileIds = ({ tileIds }: { tileIds: string[] }) => {
  const seenTileIds = new Set<string>();
  const uniqueTileIds: string[] = [];

  for (const tileId of tileIds) {
    if (seenTileIds.has(tileId)) {
      continue;
    }

    seenTileIds.add(tileId);
    uniqueTileIds.push(tileId);
  }

  return uniqueTileIds;
};

export const getTileIdsFromSlotLayout = ({
  availableTileIds,
  tileSlotLayout,
}: ReadTileSlotLayoutParams): string[] => {
  return getUniqueTileIds({
    tileIds: getTileRowsFromSlotLayout({ availableTileIds, tileSlotLayout })
      .flat()
      .filter((tileId): tileId is string => !!tileId),
  });
};

export const getTileRowsFromSlotLayout = ({
  availableTileIds,
  tileSlotLayout,
}: ReadTileSlotLayoutParams): Array<Array<string | null>> => {
  if (!isValidTileSlotLayoutHeader(tileSlotLayout)) {
    return [];
  }

  const availableTileIdSet = availableTileIds ? new Set(availableTileIds) : undefined;
  const slotCountLimit = availableTileIdSet
    ? availableTileIdSet.size + (tileSlotLayout.rows_per_slide * tileSlotLayout.cols_per_row)
    : DEFAULT_MAX_PERSISTED_SLOT_COUNT;
  const rowCountLimit = Math.ceil(slotCountLimit / tileSlotLayout.cols_per_row);

  if (tileSlotLayout.slots.length > rowCountLimit) {
    return [];
  }

  const seenTileIds = new Set<string>();
  const safeRows: Array<Array<string | null>> = [];
  let visitedSlots = 0;

  for (const row of tileSlotLayout.slots) {
    if (!Array.isArray(row) || row.length !== tileSlotLayout.cols_per_row) {
      return [];
    }

    if (visitedSlots >= slotCountLimit) {
      break;
    }

    const safeRow: Array<string | null> = [];
    for (const tileId of row) {
      if (visitedSlots >= slotCountLimit) {
        break;
      }

      visitedSlots += 1;
      if (
        !tileId
        || typeof tileId !== "string"
        || tileId.length > DEFAULT_MAX_PERSISTED_TILE_ID_LENGTH
        || seenTileIds.has(tileId)
        || (availableTileIdSet && !availableTileIdSet.has(tileId))
      ) {
        safeRow.push(null);
        continue;
      }

      seenTileIds.add(tileId);
      safeRow.push(tileId);
    }

    safeRows.push(safeRow);
  }

  if (availableTileIdSet?.size && !seenTileIds.size) {
    return [];
  }

  return safeRows;
};

export const sanitizeTileSlotLayout = ({
  availableTileIds,
  tileSlotLayout,
}: ReadTileSlotLayoutParams): CredentialTileSlotLayoutV1 | null => {
  if (!isValidTileSlotLayoutHeader(tileSlotLayout)) {
    return null;
  }

  const safeRows = getTileRowsFromSlotLayout({
    availableTileIds,
    tileSlotLayout,
  });

  if (tileSlotLayout.slots.length && !safeRows.length) {
    return null;
  }

  return {
    ...tileSlotLayout,
    slots: safeRows,
  };
};

export const createTileSlotLayout = ({
  tileIds,
  rowsPerSlide,
  colsPerRow,
  selectionTileCount,
}: CreateTileSlotLayoutParams): CredentialTileSlotLayoutV1 => {
  const uniqueTileIds = getUniqueTileIds({ tileIds });
  const normalizedGridSelection = normalizeLegacyGridSelection({
    colsPerRow,
    rowsPerSlide,
    tileCount: selectionTileCount ?? uniqueTileIds.length,
  });
  const slots: CredentialTileSlotLayoutV1["slots"] = [];

  for (let index = 0; index < uniqueTileIds.length; index += normalizedGridSelection.colsPerRow) {
    const row: Array<string | null> = uniqueTileIds.slice(index, index + normalizedGridSelection.colsPerRow);
    while (row.length < normalizedGridSelection.colsPerRow) {
      row.push(null);
    }
    slots.push(row);
  }

  return {
    cols_per_row: normalizedGridSelection.colsPerRow,
    rows_per_slide: normalizedGridSelection.rowsPerSlide,
    slots,
    version: 1,
  };
};

export const moveTileInSlotLayout = ({
  activeId,
  overId,
  tileSlotLayout,
}: MoveTileInSlotLayoutParams): CredentialTileSlotLayoutV1 => {
  if (activeId === overId) {
    return tileSlotLayout;
  }

  const safeRows = getTileRowsFromSlotLayout({ tileSlotLayout });
  if (!safeRows.length) {
    return tileSlotLayout;
  }

  const flattenedSlots = safeRows.flat();
  const activeIndex = flattenedSlots.findIndex((tileId) => tileId === activeId);
  const overIndex = flattenedSlots.findIndex((tileId) => tileId === overId);

  if (activeIndex < 0 || overIndex < 0) {
    return tileSlotLayout;
  }

  const nextSlots = flattenedSlots.slice();
  const [activeTileId] = nextSlots.splice(activeIndex, 1);
  nextSlots.splice(overIndex, 0, activeTileId);
  const rows: CredentialTileSlotLayoutV1["slots"] = [];

  for (let index = 0; index < nextSlots.length; index += tileSlotLayout.cols_per_row) {
    rows.push(nextSlots.slice(index, index + tileSlotLayout.cols_per_row));
  }

  return {
    ...tileSlotLayout,
    slots: rows,
  };
};

export const createTileSlotLayoutFromLegacyGrid = ({
  colsPerRow,
  gridTemplateAreas,
  prefix,
  rowsPerSlide,
}: {
  colsPerRow: number | undefined;
  gridTemplateAreas: string | undefined;
  prefix: string;
  rowsPerSlide: number | undefined;
}): CredentialTileSlotLayoutV1 => {
  const tileIds = getUniqueLegacyGridTileIds({
    gridTemplateAreas,
    prefix,
  });
  const firstSlideTileCount = rowsPerSlide && colsPerRow
    ? Math.min(tileIds.length, Math.max(0, rowsPerSlide * colsPerRow))
    : tileIds.length;

  return createTileSlotLayout({
    colsPerRow,
    rowsPerSlide,
    selectionTileCount: firstSlideTileCount,
    tileIds,
  });
};

export const syncTileSlotLayoutWithTileIds = ({
  colsPerRow,
  rowsPerSlide,
  tileIds,
  tileSlotLayout,
}: {
  colsPerRow: number | undefined;
  rowsPerSlide: number | undefined;
  tileIds: string[];
  tileSlotLayout: CredentialTileSlotLayoutV1 | null | undefined;
}): CredentialTileSlotLayoutV1 => {
  const availableTileIds = new Set(tileIds);
  const safeTileSlotLayout = sanitizeTileSlotLayout({ availableTileIds: tileIds, tileSlotLayout });
  const existingTileIds = getTileIdsFromSlotLayout({ availableTileIds: tileIds, tileSlotLayout: safeTileSlotLayout })
    .filter((tileId) => availableTileIds.has(tileId));
  const existingTileIdSet = new Set(existingTileIds);
  const missingTileIds = tileIds.filter((tileId) => !existingTileIdSet.has(tileId));

  return createTileSlotLayout({
    colsPerRow: safeTileSlotLayout?.cols_per_row ?? colsPerRow,
    rowsPerSlide: safeTileSlotLayout?.rows_per_slide ?? rowsPerSlide,
    tileIds: [
      ...existingTileIds,
      ...missingTileIds,
    ],
  });
};

export const getGroupedAssignmentsByTileIds = ({
  groupedAssignments,
  groupedAssignmentsRefsDelimiter,
  tileIds,
}: {
  groupedAssignments: GroupedAssignments[];
  groupedAssignmentsRefsDelimiter: string;
  tileIds: string[];
}): Record<string, GroupedAssignments> => {
  const groupedAssignmentsById: Record<string, GroupedAssignments> = {};

  for (const tileId of tileIds) {
    const assignmentIds = tileId.split(groupedAssignmentsRefsDelimiter);
    const assignmentIdSet = new Set(assignmentIds);
    const groupedAssignment = groupedAssignments.find((assignment) => {
      const assignmentRefKeys = Object.keys(assignment).filter((key) => !GROUPED_ASSIGNMENT_BASE_KEYS.has(key));

      return assignmentIdSet.size === assignmentIds.length
        && assignmentRefKeys.length === assignmentIds.length
        && assignmentRefKeys.every((assignmentId) => assignmentIdSet.has(assignmentId));
    });

    if (groupedAssignment) {
      groupedAssignmentsById[tileId] = groupedAssignment;
    }
  }

  return groupedAssignmentsById;
};
